/* Diamond quick view — instant detail panel for stones that have no product page yet.

   The old flow blocked every click on a full-screen "Give us a few seconds…"
   overlay while the bridge created the Shopify product (measured 10–24 s). A
   product is only needed for the CART, not to LOOK at a stone: search-live
   already returns photo, grading, measurements, certificate and price. So the
   panel opens at once from that data and the product is created behind it.

   When the product is created:
     • after DWELL ms with the panel open (default 2000) — a glance-and-close does
       not create a product, so browsing many stones does not flood the catalogue;
     • immediately when the shopper shows intent (pointer/focus/touch on a CTA).
   If a CTA is clicked before the product is ready, only that button waits.

   Public API (used by diamond-search.js):
     vaalbergQuickview.open(stone, { proxy, priceText, trigger, push })
     vaalbergQuickview.openById(id, { proxy, formatPrice })
   `stone` is a search-live result row. */
(function () {
  if (window.vaalbergQuickview) return;

  var ROOT = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/');
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/';

  var pending = {};          // diamondId → Promise<{ url, variantId }>
  var created = {};          // diamondId → url of the product created in this session
  var panel = null;          // root element of the open panel
  var current = null;        // { d, opts }
  var dwellTimer = null;
  var pushedState = false;
  var lastFocus = null;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function cap(s) { return s ? s.charAt(0) + s.slice(1).toLowerCase() : s; }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  var GRADE = { EX: 'Excellent', VG: 'Very good', G: 'Good', GD: 'Good', F: 'Fair', P: 'Poor', ID: 'Ideal' };
  function grade(v) { if (!v) return null; var k = String(v).toUpperCase(); return GRADE[k] || cap(String(v)); }
  var FLUOR = { NON: 'None', NONE: 'None', FNT: 'Faint', MED: 'Medium', STG: 'Strong', VST: 'Very strong', SLT: 'Slight', VSL: 'Very slight' };
  function fluor(v) { if (!v) return null; var k = String(v).toUpperCase(); return FLUOR[k] || cap(String(v)); }
  function num(v, digits) { var n = parseFloat(v); return isNaN(n) ? null : n.toFixed(digits); }

  // ---- product creation (idempotent per stone, shared across opens) ----
  function ensure(id, proxy) {
    if (pending[id]) return pending[id];
    var started = Date.now();
    function once() {
      return fetch(proxy + '/stone?id=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { status: r.status, j: j }; }); })
        .then(function (res) {
          if (res.status === 200 && res.j && res.j.url) return res.j;
          // 409 = another request holds the create-lock; 429 = per-IP create limit.
          if ((res.status === 409 || res.status === 429) && Date.now() - started < 60000) {
            return wait(res.status === 429 ? 5000 : 1500).then(once);
          }
          var err = new Error((res.j && res.j.error) || ('http_' + res.status));
          err.code = (res.j && res.j.error) || ('http_' + res.status);
          throw err;
        });
    }
    var p = once();
    pending[id] = p;
    p.then(function (j) { if (j && j.url) created[id] = j.url; }).catch(function () { if (pending[id] === p) delete pending[id]; }); // a failure may be retried
    return p;
  }

  // ---- cart (same drawer hand-off as the PDP, see diamond-pro.js initCart) ----
  function drawerSectionId() {
    var d = document.querySelector('cart-drawer');
    var w = d && d.closest && d.closest('[id^="shopify-section-"]');
    if (w) return w.id.replace('shopify-section-', '');
    var byType = document.querySelector('[data-section-type="cart-drawer"]');
    var wrap = byType && byType.closest && byType.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : null;
  }
  function injectDrawer(sections, sid) {
    if (!sid || !sections || !sections[sid]) return false;
    var live = document.querySelector('cart-drawer');
    if (!live) return false;
    var fresh;
    try { fresh = new DOMParser().parseFromString(sections[sid], 'text/html').querySelector('cart-drawer'); }
    catch (e) { return false; }
    if (!fresh) return false;
    try { live.replaceWith(fresh); } catch (e) { return false; }
    return true;
  }
  function openDrawer() {
    try {
      var d = document.querySelector('cart-drawer');
      if (d && typeof d.openCartDrawer === 'function') { d.openCartDrawer(); return true; }
    } catch (e) { /* fall through to the cart page */ }
    return false;
  }
  function refreshCartCount() {
    fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('cart-count, [data-cart-count]').forEach(function (el) {
          el.setAttribute('data-cart-count', cart.item_count);
          el.textContent = cart.item_count;
          if (el.classList) el.classList.toggle('is-empty', cart.item_count === 0);
        });
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }).catch(function () { /* best-effort */ });
  }
  function cartUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart_url) || (ROOT + 'cart');
  }
  function variantIdFor(p) {
    if (p.variantId) return Promise.resolve(p.variantId);
    // Older bridge responses carry no variant id — read it from the product JSON.
    return fetch(ROOT + p.url.replace(/^\//, '') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (j) { return j && j.variants && j.variants[0] && j.variants[0].id; });
  }

  // ---- markup ----
  function specRow(label, value) {
    return value ? '<div class="dqv__spec"><dt>' + esc(label) + '</dt><dd>' + esc(value) + '</dd></div>' : '';
  }
  function build(d, priceText) {
    var carat = num(d.carat, 2);
    var title = (carat ? carat + ' ct ' : '') + cap(d.shape || 'Diamond') + ' Diamond';
    var line = [d.color ? d.color + ' colour' : null, d.clarity ? d.clarity + ' clarity' : null,
      grade(d.cut) ? grade(d.cut) + ' cut' : null].filter(Boolean).join(' · ');
    var dims = (num(d.lengthMm, 2) && num(d.widthMm, 2))
      ? num(d.lengthMm, 2) + ' × ' + num(d.widthMm, 2) + (num(d.depthMm, 2) ? ' × ' + num(d.depthMm, 2) : '') + ' mm' : null;

    var media = d.image
      ? '<img class="dqv__img" src="' + esc(d.image) + '" alt="' + esc(title) + '">'
      : '<div class="dqv__noimg" aria-hidden="true">◇</div>';
    // No 360° tab for now: search-live's `video360` is the supplier's raw viewer link,
    // which does not embed (verified: "NoSuchKey"). The PDP builds its viewer from a
    // different, resized URL. Render the tab only if the bridge ever sends a verified
    // embeddable `videoEmbed`.
    var tabs = (d.image && d.videoEmbed)
      ? '<div class="dqv__tabs" role="group" aria-label="Media">'
        + '<button type="button" class="dqv__tab is-on" data-dqv-media="photo" aria-pressed="true">Photo</button>'
        + '<button type="button" class="dqv__tab" data-dqv-media="video" aria-pressed="false">360° view</button></div>'
      : '';

    var cert = d.certPdf
      ? '<a class="dqv__cert" href="' + esc(d.certPdf) + '" target="_blank" rel="noopener">View ' + esc(d.lab || '') + ' certificate'
        + (d.certNumber ? ' ' + esc(d.certNumber) : '') + ' ↗</a>'
      : (d.certNumber ? '<p class="dqv__cert dqv__cert--plain">' + esc(d.lab || 'Certificate') + ' ' + esc(d.certNumber) + '</p>' : '');

    return ''
      + '<div class="dqv__backdrop" data-dqv-close></div>'
      + '<aside class="dqv__panel" tabindex="-1">'
      +   '<button type="button" class="dqv__close" data-dqv-close aria-label="Close">✕</button>'
      +   '<div class="dqv__media" data-dqv-stage>' + media + '</div>' + tabs
      +   '<div class="dqv__body">'
      +     '<p class="dqv__eyebrow">Lab-grown' + (d.lab ? ' · ' + esc(d.lab) + ' certified' : '') + '</p>'
      +     '<h2 class="dqv__title" id="dqv-title">' + esc(title) + '</h2>'
      +     (line ? '<p class="dqv__grade">' + esc(line) + '</p>' : '')
      +     '<p class="dqv__price">' + esc(priceText || '') + '</p>'
      +     '<div class="dqv__cta">'
      +       '<button type="button" class="dqv__btn dqv__btn--full" data-dqv-full><span data-dqv-full-label>Full details</span></button>'
      +       '<button type="button" class="dqv__btn dqv__btn--primary" data-dqv-add><span data-dqv-add-label>Add to cart</span></button>'
      +     '</div>'
      +     '<p class="dqv__status" data-dqv-status aria-live="polite"></p>'
      +     '<dl class="dqv__specs">'
      +       specRow('Shape', cap(d.shape)) + specRow('Carat', carat ? carat + ' ct' : null)
      +       specRow('Colour', d.color) + specRow('Clarity', d.clarity)
      +       specRow('Cut', grade(d.cut)) + specRow('Polish', grade(d.polish))
      +       specRow('Symmetry', grade(d.symmetry)) + specRow('Fluorescence', fluor(d.fluorescence))
      +       specRow('Measurements', dims)
      +       specRow('Table', num(d.tablePct, 1) ? num(d.tablePct, 1) + '%' : null)
      +       specRow('Depth', num(d.depthPct, 1) ? num(d.depthPct, 1) + '%' : null)
      +     '</dl>'
      +     cert
      +     '<button type="button" class="dqv__btn dqv__btn--full dqv__btn--wide" data-dqv-full>'
      +       '<span data-dqv-full-label>Full details</span></button>'
      +     '<ul class="dqv__trust"><li>Free insured shipping</li><li>30-day returns</li><li>Inspected in Sweden</li></ul>'
      +   '</div>'
      + '</aside>';
  }

  // ---- state helpers ----
  function q(sel) { return panel ? panel.querySelector(sel) : null; }
  function setStatus(msg, kind) {
    var el = q('[data-dqv-status]'); if (!el) return;
    el.textContent = msg || '';
    el.className = 'dqv__status' + (kind ? ' dqv__status--' + kind : '');
  }
  // Full details is rendered twice (top and bottom of the panel), so every state
  // change has to hit both copies — driving only the first left the other live.
  function setBusy(btnSel, labelSel, busy, text) {
    if (!panel) return;
    panel.querySelectorAll(btnSel).forEach(function (b) {
      var l = b.querySelector(labelSel); if (!l) return;
      if (busy) { if (!b.dataset.label) b.dataset.label = l.textContent; l.textContent = text; b.classList.add('is-busy'); b.disabled = true; }
      else { if (b.dataset.label) l.textContent = b.dataset.label; b.classList.remove('is-busy'); b.disabled = false; }
    });
  }
  function markSold() {
    panel.querySelectorAll('[data-dqv-add], [data-dqv-full]').forEach(function (b) { b.disabled = true; b.classList.remove('is-busy'); });
    panel.querySelectorAll('[data-dqv-add-label]').forEach(function (l) { l.textContent = 'Just sold'; });
    setStatus('Sorry — this stone has just been sold. Every diamond is one of a kind; similar stones are in the list.', 'error');
    var t = current && current.opts.trigger;
    if (t) { var lab = t.querySelector('[data-order-label]'); if (lab) lab.textContent = 'Just sold'; t.classList.add('is-sold'); }
  }
  function fail(err, btnSel, labelSel) {
    setBusy(btnSel, labelSel, false);
    var code = err && err.code;
    if (code === 'unavailable' || code === 'not_found') return markSold();
    if (code === 'out_of_range' || code === 'incomplete') {
      return setStatus('This stone can’t be ordered online. Contact us and we’ll source it for you.', 'error');
    }
    setStatus('Something went wrong preparing this stone. Please try again.', 'error');
  }
  function startCreate() {
    if (!current) return null;
    clearTimeout(dwellTimer); dwellTimer = null;
    var p = ensure(current.d.diamondId, current.opts.proxy);
    p.catch(function () { /* surfaced when a CTA is used */ });
    return p;
  }

  // ---- actions ----
  function addToCart() {
    if (!current) return;
    var mine = current;
    setStatus('');
    setBusy('[data-dqv-add]', '[data-dqv-add-label]', true, 'Reserving this stone…');
    startCreate()
      .then(function (p) { return variantIdFor(p).then(function (vid) { return { p: p, vid: vid }; }); })
      .then(function (r) {
        if (!r.vid) throw new Error('no_variant');
        var sid = drawerSectionId();
        var body = { items: [{ id: Number(r.vid), quantity: 1 }] };
        if (sid) body.sections = [sid];
        // A product created seconds ago can lag on the storefront: retry briefly
        // before telling the shopper it failed. "Already in the bag" is final.
        var tries = 0;
        function onRetry(n) { if (n === 2) setBusy('[data-dqv-add]', '[data-dqv-add-label]', true, 'Preparing this stone…'); if (n === 6) setStatus('Almost there — Shopify is still registering this stone.', ''); }
        function post() {
          return fetch(ROOT + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }).then(function (resp) {
            return resp.json().catch(function () { return {}; }).then(function (j) {
              var final = resp.ok || /maximum quantity|already/i.test((j && j.description) || '');
              if (!final && ++tries <= 14) { onRetry(tries); return wait(1500).then(post); }
              return { ok: resp.ok, j: j, sid: sid };
            });
          });
        }
        return post();
      })
      .then(function (res) {
        if (current !== mine) return;
        setBusy('[data-dqv-add]', '[data-dqv-add-label]', false);
        if (!res.ok) {
          // inventory = 1: a second add of the same stone is rejected — it is already in the bag.
          if (/maximum quantity|already/i.test((res.j && res.j.description) || '')) { location.href = cartUrl(); return; }
          setStatus((res.j && res.j.description) || 'Could not add this stone to the cart.', 'error');
          return;
        }
        refreshCartCount();
        if (injectDrawer(res.j.sections, res.sid)) { dismiss(); if (openDrawer()) return; }
        location.href = cartUrl();
      })
      .catch(function (err) { if (current === mine) fail(err, '[data-dqv-add]', '[data-dqv-add-label]'); });
  }
  // "Full details" no longer waits for Shopify: the virtual diamond page
  // (/pages/diamond?stone=<id>) renders the same data instantly and creates the
  // product only when the shopper adds it to the cart. If the product already got
  // created during the dwell, the virtual page redirects there once it is live.
  function virtualUrl(id) { return ROOT + 'pages/diamond?stone=' + encodeURIComponent(id); }
  function fullDetails() {
    if (!current) return;
    // Always the virtual page: it renders at once and only hands over to the real
    // product page when Shopify's storefront actually serves it as available — a
    // product created seconds ago can still render as "reserved" for a moment.
    location.href = virtualUrl(current.d.diamondId);
  }
  function showMedia(kind) {
    var stage = q('[data-dqv-stage]'); if (!stage || !current) return;
    var d = current.d;
    panel.querySelectorAll('[data-dqv-media]').forEach(function (b) {
      var on = b.getAttribute('data-dqv-media') === kind;
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    stage.innerHTML = (kind === 'video' && d.videoEmbed)
      ? '<iframe class="dqv__frame" src="' + esc(d.videoEmbed) + '" title="360° view" loading="lazy" allow="autoplay; fullscreen" scrolling="no"></iframe>'
      : '<img class="dqv__img" src="' + esc(d.image) + '" alt="">';
  }

  // ---- URL: ?stone=<id> makes the panel linkable and lets Back close it ----
  function urlWithStone(id) {
    var u = new URL(location.href);
    if (id) u.searchParams.set('stone', id); else u.searchParams.delete('stone');
    return u.pathname + (u.search || '') + u.hash;
  }

  function onKey(e) {
    if (!panel) return;
    if (e.key === 'Escape') { e.preventDefault(); requestClose(); return; }
    if (e.key !== 'Tab') return;
    var f = panel.querySelectorAll('button:not([disabled]), a[href], iframe');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function close() {
    if (!panel) return;
    clearTimeout(dwellTimer); dwellTimer = null;
    document.removeEventListener('keydown', onKey, true);
    document.documentElement.classList.remove('dqv-open');
    panel.remove(); panel = null; current = null;
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* gone */ } }
    lastFocus = null;
  }
  // Programmatic close (e.g. the cart drawer takes over): drop ?stone= in place.
  // history.back() is async and would race the drawer opening.
  function dismiss() {
    if (new URL(location.href).searchParams.get('stone')) history.replaceState(history.state, '', urlWithStone(null));
    pushedState = false;
    close();
  }
  // User-initiated close: unwind the history entry we pushed, or just drop the param.
  function requestClose() {
    if (pushedState) { pushedState = false; history.back(); /* popstate → close() */ }
    else { history.replaceState(history.state, '', urlWithStone(null)); close(); }
  }
  window.addEventListener('popstate', function () {
    if (panel && !new URL(location.href).searchParams.get('stone')) { pushedState = false; close(); }
  });

  function open(d, opts) {
    if (!d || !d.diamondId) return;
    opts = opts || {};
    if (panel) close();
    lastFocus = opts.trigger || document.activeElement;
    current = { d: d, opts: { proxy: (opts.proxy || '').replace(/\/$/, ''), trigger: opts.trigger || null } };

    panel = document.createElement('div');
    panel.className = 'dqv';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'dqv-title');
    panel.innerHTML = build(d, opts.priceText);
    document.body.appendChild(panel);
    document.documentElement.classList.add('dqv-open');
    // rAF is throttled in background tabs (a deep link opened in a new tab) — the
    // timer guarantees the panel still slides in.
    var mine = panel;
    function slideIn() { if (panel === mine) panel.classList.add('is-in'); }
    requestAnimationFrame(slideIn); setTimeout(slideIn, 60);

    panel.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-dqv-close]')) { requestClose(); return; }
      if (t.closest('[data-dqv-add]')) { addToCart(); return; }
      if (t.closest('[data-dqv-full]')) { fullDetails(); return; }
      var m = t.closest('[data-dqv-media]');
      if (m) showMedia(m.getAttribute('data-dqv-media'));
    });
    // Intent → start creating now rather than waiting out the dwell timer.
    ['pointerenter', 'focusin', 'touchstart'].forEach(function (ev) {
      var cta = q('.dqv__cta');
      if (cta) cta.addEventListener(ev, function () { startCreate(); }, { passive: true });
    });
    document.addEventListener('keydown', onKey, true);
    var closeBtn = q('.dqv__close'); if (closeBtn) closeBtn.focus({ preventScroll: true });

    if (opts.push !== false) { history.pushState({ dqv: d.diamondId }, '', urlWithStone(d.diamondId)); pushedState = true; }
    else pushedState = false;

    var dwell = (typeof opts.dwell === 'number') ? opts.dwell : 2000;
    dwellTimer = setTimeout(startCreate, dwell);
  }

  // Deep link: the stone is not in memory → ask the bridge for its display data.
  function openById(id, opts) {
    opts = opts || {};
    var proxy = (opts.proxy || '').replace(/\/$/, '');
    return fetch(proxy + '/stone-info?id=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.diamondId) { history.replaceState(history.state, '', urlWithStone(null)); return false; }
        if (d.url) { location.replace(ROOT + d.url.replace(/^\//, '')); return true; } // already a real page
        open(d, { proxy: proxy, priceText: opts.formatPrice ? opts.formatPrice(d) : '', push: false });
        return true;
      })
      .catch(function () { return false; });
  }

  window.vaalbergQuickview = { open: open, openById: openById, close: requestClose };
})();
